// The per-game sections' aggregator, one per stage: every scanned game of
// the last 30 days that reached the stage, with what the section's three
// cards draw of it, and the period's figures to set it against. The section
// shows the selected game (`?game=`, the latest without one), picked from
// this result by `lastGameOf`. Registered with the game store by this
// plugin's setup.
import { stageResult, type StageResult } from './stage-result.ts';
import type { VerdictFacts } from '@chess/api-verdict';
import { positionKey } from '@chess/api-insights';
import { endingDetail, middlegameDetail, type EndingDetail, type MiddlegameDetail, type TypeRecord } from './stage-detail.ts';
import { aggregatorId, type Accumulator, type Aggregator, type Color, type EndingType, type GameFacts, type GameOutcome, type Fen, type GradedMove, type GroundCause, type Phase, type San, type StageCurve, type StageErrors, type Uci } from '@chess/api-model';

/** The latest game in the stage, as the cards and the section's link need it. */
export interface LastGame {
  id: string;
  /** Epoch milliseconds. */
  playedAt: number;
  opponent?: string;
  colour: Color;
  outcome: GameOutcome;
  /** The opening's family, e.g. "Sicilian Defense". */
  opening?: string;
  /** The opening's variation as the book writes it. */
  variation?: string;
  /** The review's address; the section opens it at the stage (`stagePly`). */
  reviewPath?: string;
  curve: StageCurve;
  errors?: StageErrors;
  /** Pawns: the score after the opening, or the score change across the middlegame or the ending. */
  value?: number;
  /** How the stage started and ended, and its classification (the stage result card). */
  result?: StageResult;
  /** The reader's accuracy over the stage, 0–100 (`GameFacts.stageAccuracy`); absent with too few of the reader's moves in it. */
  accuracy?: number;
  /** The middlegame's third card: the mistakes by cause. */
  middlegame?: MiddlegameDetail;
  /** The ending's third card: the ending's type, length and case. */
  ending?: EndingDetail;
  /** The opening: the reader's moves graded, the strip under the score (`GameFacts.openingMoves`). */
  moves?: GradedMove[];
  /** The opening: where the game left the book and who left it. */
  bookExit?: { ply: number; by: Color };
  /** The opening: a move of this game's that the reader also got wrong in the same position in other games (the third card). */
  habit?: GameHabit;
  /** What the verdict API reads of the game (the result card's caption). */
  facts: VerdictFacts;
}

/**
 * A habit seen in one game: a position of its opening where the reader gave
 * up score (`GameFacts.openingSlips`) and did so in other games too, the
 * same grouping as the Openings screen's "Positions you keep getting wrong".
 */
export interface GameHabit {
  /** This game's slip: its 1-based ply and the move played. */
  ply: number;
  san: San;
  /** The position before the move and the moves to it, from this game. */
  fen: Fen;
  line: San[];
  /** The engine's move there. */
  best: Uci;
  /** Every game with a slip in the position, oldest first, this one included. */
  games: HabitGame[];
}

/** One game's slip in a habit's position. */
export interface HabitGame {
  id: string;
  playedAt: number;
  ply: number;
  san: San;
  /** Pawns given up against the engine's move. */
  loss: number;
  reviewPath?: string;
  /** The game the section shows. */
  current: boolean;
}

/** What the section draws: one game in the stage and the figures to set it against. */
export interface LastGameResult {
  /** Undefined when the game shown did not reach the stage, is not scanned, or the period has none. */
  game?: LastGame;
  /** Pawns, the same measure averaged over the period's scanned games (the opening: those of the same family); undefined without one. */
  average?: number;
  /** How many games the average is over. */
  averageOf: number;
  /** The middlegame: the period's most frequent cause of a middlegame gone wrong, with its share. */
  usualCause?: UsualCause;
  /** The ending: the period's record in the shown game's kind of ending. */
  typeRecord?: TypeRecord;
  /** The middlegame: the period's middlegames gone wrong by cause. */
  causes?: Partial<Record<GroundCause, number>>;
  /** The stage's measure game by game, oldest first, the shown game marked: the opening's same family, a later stage every game that reached it. */
  history?: HistoryPoint[];
}

/** One game on the "over time" chart. */
export interface HistoryPoint {
  id: string;
  playedAt: number;
  /** Pawns: the stage's measure (`stageValue`). */
  value: number;
  outcome: GameOutcome;
  /** The review at the stage (`stageHref`). */
  href?: string;
  current: boolean;
}

/** The period's most frequent cause of lost ground: how many of the middlegames gone wrong it explains. */
export interface UsualCause {
  cause: GroundCause;
  games: number;
  of: number;
}

/** An average and how many games it is over. */
export interface Average {
  average: number;
  of: number;
}

/** The aggregator's result: every game of the period in the stage, and the averages to set one against. */
export interface StageGames {
  /** Each scanned game that reached the stage, by id. */
  games: Readonly<Record<string, LastGame>>;
  /** The latest of them; undefined while none did. */
  latest?: string;
  /** A later stage: the period's average over every scanned game that reached it. */
  overall?: Average;
  /** The opening: the average per opening family. */
  byOpening: Readonly<Record<string, Average>>;
  /** The middlegame: the period's middlegames gone wrong by cause. */
  causes?: Partial<Record<GroundCause, number>>;
  /** The ending: the period's endings by type and result. */
  byType?: Partial<Record<EndingType, TypeRecord>>;
}

/** Why the section has no game to draw for the selected game. */
export type StageStatus = 'shown' | 'unscanned' | 'ended';

/** Whether a game can be drawn in the stage: not scanned yet, ended before it, or shown. */
export function stageStatus(game: Pick<GameFacts, 'scanned' | 'stageCurves'> | undefined, stage: Phase): StageStatus {
  if (!game?.scanned) return 'unscanned';
  return game.stageCurves?.[stage] ? 'shown' : 'ended';
}

/**
 * The section's data for the game `id` (the latest in the stage without
 * one): the game when it reached the stage, with the average it is set
 * against; without it, only the period's figures.
 */
export function lastGameOf(result: StageGames | undefined, stage: Phase, id?: string): LastGameResult | undefined {
  if (!result) return undefined;
  const key = id ?? result.latest;
  const game = key ? result.games[key] : undefined;
  const peers = stage === 'opening' ? (game?.opening ? result.byOpening[game.opening] : undefined) : result.overall;
  const typeRecord = game?.ending?.type ? result.byType?.[game.ending.type] : undefined;
  const history: HistoryPoint[] = game
    ? Object.values(result.games)
        .filter((g) => g.value !== undefined && (stage !== 'opening' || g.opening === game.opening))
        .sort((a, b) => a.playedAt - b.playedAt)
        .map((g) => ({ id: g.id, playedAt: g.playedAt, value: g.value!, outcome: g.outcome, href: stageHref(g), current: g.id === game.id }))
    : [];
  return { game, average: game ? peers?.average : undefined, averageOf: game ? (peers?.of ?? 0) : 0, usualCause: usualCauseOf(result.causes), typeRecord, causes: result.causes, history };
}

/** The most frequent cause, ties to the pie's order (tactic, missed, time, positional). */
export function usualCauseOf(causes: Partial<Record<GroundCause, number>> | undefined): UsualCause | undefined {
  if (!causes) return undefined;
  const order: GroundCause[] = ['tactic', 'missed', 'time', 'positional'];
  let best: UsualCause | undefined;
  let of = 0;
  for (const cause of order) {
    const n = causes[cause] ?? 0;
    of += n;
    if (n > 0 && (!best || n > best.games)) best = { cause, games: n, of: 0 };
  }
  return best ? { ...best, of } : undefined;
}

const ERRORS: Record<Phase, (g: GameFacts) => StageErrors | undefined> = {
  opening: (g) => g.openingErrors,
  middlegame: (g) => g.middlegame,
  ending: (g) => g.endgame,
};

/** The stage's measure for one game: the score after the opening, or the score change across a later stage. */
export function stageValue(stage: Phase, game: GameFacts): number | undefined {
  return stage === 'opening' ? game.openingScore : game.stageSwings?.[stage];
}

/** The ply the section's link opens: the reader's stage starts with the move after `fromPly`. */
export function stagePly(curve: StageCurve): number {
  return curve.fromPly + 1;
}

/** The review address of the game, opened at the stage. */
export function stageHref(game: LastGame): string | undefined {
  return game.reviewPath ? `${game.reviewPath}#ply=${stagePly(game.curve)}` : undefined;
}

export class LastGameAggregator implements Aggregator<StageGames | undefined> {
  readonly id: ReturnType<typeof aggregatorId>;
  readonly stage: Phase;

  constructor(stage: Phase) {
    this.stage = stage;
    this.id = aggregatorId(`last-game-${stage}`);
  }

  start(): Accumulator<StageGames | undefined> {
    const stage = this.stage;
    const all: GameFacts[] = [];
    return {
      add: (game) => {
        all.push(game);
      },
      result: () => {
        if (!all.length) return undefined;
        const games: Record<string, LastGame> = {};
        let latest: GameFacts | undefined;
        for (const g of all) {
          const curve = g.stageCurves?.[stage];
          if (!g.scanned || !curve) continue;
          games[g.id] = {
            id: g.id,
            playedAt: g.playedAt,
            opponent: g.opponent,
            colour: g.colour,
            outcome: g.outcome,
            opening: g.opening,
            variation: g.variation,
            reviewPath: g.reviewPath,
            curve,
            errors: ERRORS[stage](g),
            value: stageValue(stage, g),
            result: stageResult(stage, g),
            accuracy: g.stageAccuracy?.[stage],
            middlegame: stage === 'middlegame' ? middlegameDetail(g) : undefined,
            ending: stage === 'ending' ? endingDetail(g, curve) : undefined,
            moves: stage === 'opening' ? g.openingMoves : undefined,
            bookExit: stage === 'opening' ? g.bookExit : undefined,
            facts: g,
          };
          if (!latest || g.playedAt > latest.playedAt) latest = g;
        }
        if (stage === 'opening') attachHabits(all, games);
        // The opening is set against the same family's games; a later stage against every game that reached it.
        const measured = all.filter((g) => g.scanned && stageValue(stage, g) !== undefined);
        const byOpening: Record<string, Average> = {};
        if (stage === 'opening') {
          const sums = new Map<string, { sum: number; of: number }>();
          for (const g of measured) {
            if (!g.opening) continue;
            const s = sums.get(g.opening) ?? { sum: 0, of: 0 };
            s.sum += stageValue(stage, g)!;
            s.of += 1;
            sums.set(g.opening, s);
          }
          for (const [opening, s] of sums) byOpening[opening] = { average: s.sum / s.of, of: s.of };
        }
        const overall =
          stage !== 'opening' && measured.length ? { average: measured.reduce((n, g) => n + stageValue(stage, g)!, 0) / measured.length, of: measured.length } : undefined;
        let causes: Partial<Record<GroundCause, number>> | undefined;
        if (stage === 'middlegame') {
          causes = {};
          for (const g of all) if (g.scanned && g.groundCause) causes[g.groundCause] = (causes[g.groundCause] ?? 0) + 1;
        }
        let byType: Partial<Record<EndingType, TypeRecord>> | undefined;
        if (stage === 'ending') {
          byType = {};
          for (const g of all) {
            if (!g.scanned || !g.endingType || !g.endingResult) continue;
            const record = (byType[g.endingType] ??= {});
            record[g.endingResult] = (record[g.endingResult] ?? 0) + 1;
          }
        }
        return { games, latest: latest?.id, overall, byOpening, causes, byType };
      },
    };
  }
}

/**
 * Gives each game its habit: of its opening slips, the position slipped in by
 * the most games (two or more, this one included), then the costliest. Each
 * game counts once per position, by its first slip there.
 */
function attachHabits(all: readonly GameFacts[], games: Record<string, LastGame>): void {
  const byPosition = new Map<string, Array<Omit<HabitGame, 'current'>>>();
  for (const g of all) {
    if (!g.scanned) continue;
    const seen = new Set<string>();
    for (const slip of g.openingSlips ?? []) {
      const key = positionKey(slip.fen);
      if (seen.has(key)) continue;
      seen.add(key);
      const list = byPosition.get(key) ?? [];
      list.push({ id: g.id, playedAt: g.playedAt, ply: slip.ply, san: slip.san, loss: slip.before - slip.after, reviewPath: g.reviewPath });
      byPosition.set(key, list);
    }
  }
  for (const g of all) {
    const game = games[g.id];
    if (!game) continue;
    let pick: { habit: GameHabit; cost: number } | undefined;
    for (const slip of g.openingSlips ?? []) {
      const others = byPosition.get(positionKey(slip.fen)) ?? [];
      if (others.length < 2 || !others.some((o) => o.id === g.id && o.ply === slip.ply)) continue;
      const cost = slip.before - slip.after;
      if (pick && (others.length < pick.habit.games.length || (others.length === pick.habit.games.length && cost <= pick.cost))) continue;
      const list = [...others].sort((a, b) => a.playedAt - b.playedAt).map((o) => ({ ...o, current: o.id === g.id }));
      pick = { habit: { ply: slip.ply, san: slip.san, fen: slip.fen, line: slip.line, best: slip.best, games: list }, cost };
    }
    if (pick) game.habit = pick.habit;
  }
}
